import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";

import moment from "moment";
import Moment from "react-moment";

import {
  makeStyles,
  withStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Paper,
  InputBase,
  Fab,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";

import LoadingButton from "../../Components/LoadingButton";

import { refreshPoolStake } from "../../apiFunctions";
import { getBalanceAndSymbol } from "../../ethereumFunctions";
import { parseTokenDecimal, setPoolReward } from "../StackingFunctions";
import { quoteRemoveLiquidity } from "../../Liquidity/LiquidityFunctions";

import useIcon from "../../hooks/useIcon";

import "./Stacking.scss";

const useStyles = makeStyles((theme) => ({
  primaryText: {
    fill: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },

  calcIcon: {
    fill: theme.palette.grey[500],
    "&:hover": {
      fill: theme.palette.grey[400],
    },
  },

  hoverColor: {
    "&:hover": {
      fill: theme.palette.grey[500],
      color: theme.palette.grey[500],
    },
  },

  textDecorate: {
    textDecorationLine: "underline",
    textDecorationThickness: "initial",
    textDecorationStyle: "dotted",
    textUnderlineOffset: "0.1em",
  },

  infoBox: {
    borderRadius: "14px",
    border: `1px solid ${theme.palette.grey[300]}`,
    minHeight: "124px",
    height: "100%",
    padding: theme.spacing(2),
  },

  rewardInfo: {
    borderRadius: "14px",
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
  },

  infoBoxTitle: {
    textTransform: "uppercase",
    fontWeight: 500,
    lineHeight: 1,
    color: theme.palette.grey[500],
    marginBottom: theme.spacing(1),
  },

  infoBoxBTN: {
    boxShadow: "none",
    borderRadius: "14px",
    minWidth: "max-content",
  },

  infoBoxCaption: {
    color: theme.palette.grey[500],
  },

  infoLoadingBTNBox: {
    margin: "0px -8px",
  },

  infoBoxLink: {
    color: `${theme.palette.primary.main} !important`,
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  infoBoxRefreshIcon: {
    fill: `${theme.palette.primary.main}`,
    marginLeft: theme.spacing(1),
    cursor: "pointer",
  },
  grid: {
    display: "flex",
    flexFlow: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  input: {
    ...theme.typography.h5,
    width: "100%",
  },
  fab_max: {
    minWidth: "initial",
    height: "initial",
    fontSize: "10px",
    padding: "0px 4px",
    marginLeft: theme.spacing(0.5),
    boxShadow: "none",
    borderRadius: theme.spacing(1),
  },
}));

const MuiAccordion = withStyles({
  root: {
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "auto",
    },
  },
  expanded: {},
})(Accordion);

const MuiAccordionSummary = withStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    minHeight: 56,
    "&$expanded": {
      minHeight: 56,
    },
  },
  content: {
    "&$expanded": {
      margin: "12px 0",
    },
  },
  expanded: {},
}))(AccordionSummary);

const MuiAccordionDetails = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(AccordionDetails);

function StackingListView({ item, network, active, handleChange, expired }) {
  const [iconImage] = useIcon();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [supply, setSupply] = useState([0, 0, 0]);
  const [rewardAmount, setRewardAmount] = useState("");

  const [rewardPerSeconds, setRewardPerSeconds] = useState(0);
  const [remainingReward, setRemainingReward] = useState(0);

  const [coin1, setCoin1] = useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
    decimal: undefined,
  });

  // Determines whether the button should be enabled or not
  const isStackButtonEnabled = () => {
    // If both coins have been selected, and a valid float has been entered which is less than the user's balance, then return true
    const parsedInput = parseFloat(rewardAmount);
    return coin1.address && !isNaN(parsedInput) && 0 < parsedInput;
  };

  const setReward = () => {
    console.log("Attempting to token set reward...");

    setLoading(true);

    setPoolReward(
      network.account,
      coin1.address,
      rewardAmount,
      item.id,
      network.stacking,
      network.signer,
      (res) => {
        if (res.status) {
          setLoading(false);

          enqueueSnackbar("Set Reward", { variant: "success", autoHideDuration: 5000, });

          refreshPoolStake(network.chainID);
        } else {
          setLoading(false);
          enqueueSnackbar("Set Reward Failed (" + res.e.reason + ")", {
            variant: "error",
            autoHideDuration: 5000,
          });
        }
      }
    ).catch((e) => {
      setLoading(false);
      enqueueSnackbar("Set Reward Failed (" + e.reason + ")", {
        variant: "error",
        autoHideDuration: 5000,
      });
    });
  };

  const calcRewardPerSeconds = () => {
    let currentTimeStamp = moment().unix();
    if (!expired) {
      const finishTime = +item.period_finish - currentTimeStamp;

      parseTokenDecimal(
        item.rewards_token.address,
        item.reward_rate,
        network.signer
      ).then((res) => {
        setRewardPerSeconds(+res);
        setRemainingReward(finishTime * +res);
      });
    }
  };

  useEffect(() => {
    getBalanceAndSymbol(
      network.account,
      item.rewards_token.address,
      network.wethAddress,
      network.coins
    ).then((data) => {
      setCoin1({
        address: item.rewards_token.address,
        symbol: item.rewards_token.symbol,
        balance: data.balance,
        decimal: +item.rewards_token.decimals,
      });

      if (data.balance) {
        setRewardAmount(
          Number(data.balance)
            .toFixed(item.rewards_token.decimals)
            .replace(/\.?0+$/, "")
        );
      }
    });

    // format total supply
    parseTokenDecimal(
      item.staking_token.address,
      `${item.total_supply}`,
      network.signer
    ).then((res) => {
      if (
        item.staking_token.lptokens &&
        item.staking_token.lptokens.token0.address &&
        item.staking_token.lptokens.token1.address
      ) {
        quoteRemoveLiquidity(
          item.staking_token.lptokens.token0.address,
          item.staking_token.lptokens.token1.address,
          res,
          network.factory,
        ).then((data) => {
          setSupply(data);
        });
      } else {
        setSupply([res, 0, 0]);
      }
    });

    calcRewardPerSeconds();
  }, []);

  useEffect(() => {
    if (active) {
      if (coin1.balance) {
        setRewardAmount(
          Number(coin1.balance)
            .toFixed(coin1.decimal)
            .replace(/\.?0+$/, "")
        );
      }
    }
  }, [active]);

  return (
    <>
      <MuiAccordion
        square
        expanded={active}
        onChange={handleChange(`panel${item.id}`)}
      >
        <MuiAccordionSummary
          aria-controls={`panel${item.id}`}
          id={`panel${item.id}d-header`}
        >
          <table className="w-100 admin-stacking-list">
            <tbody>
              <tr>
                <td className="id">
                  <Typography variant="caption" className="lh-1">
                    ID
                  </Typography>

                  <Typography variant="body1" className="lh-1 fw-bold">
                    #{item.id}
                  </Typography>
                </td>
                <td className="name-icon">
                  <div className="d-flex align-items-center">
                    <div className="multi-image-box">
                      {item.rewards_token.type === "token" ? (
                        <img
                          src={iconImage(item.rewards_token.symbol)}
                          alt={item.rewards_token.symbol}
                          className="image1"
                        />
                      ) : (
                        <>
                          <img
                            src={iconImage(
                              item.rewards_token.lptokens.token0.symbol
                            )}
                            alt={item.rewards_token.lptokens.token0.symbol}
                            className="image1"
                          />
                          <img
                            src={iconImage(
                              item.rewards_token.lptokens.token1.symbol
                            )}
                            alt={item.rewards_token.lptokens.token1.symbol}
                            className="image2"
                          />
                        </>
                      )}

                      {item.staking_token.type === "token" ? (
                        <img
                          src={iconImage(item.staking_token.symbol)}
                          alt={item.staking_token.symbol}
                          className="image3"
                        />
                      ) : (
                        <>
                          <img
                            src={iconImage(
                              item.staking_token.lptokens.token0.symbol
                            )}
                            alt={item.staking_token.lptokens.token0.symbol}
                            className="image3"
                          />
                          <img
                            src={iconImage(
                              item.staking_token.lptokens.token1.symbol
                            )}
                            alt={item.staking_token.lptokens.token1.symbol}
                            className="image4"
                          />
                        </>
                      )}
                    </div>

                    <div className="ms-2 d-flex flex-column">
                      <Typography variant="caption">
                        Earn{" "}
                        {item.rewards_token.type === "token"
                          ? item.rewards_token.symbol
                          : `${item.rewards_token.lptokens.token0.symbol}-${item.rewards_token.lptokens.token1.symbol}`}
                      </Typography>
                      <Typography variant="caption">
                        Stake{" "}
                        {item.staking_token.type === "token"
                          ? item.staking_token.symbol
                          : `${item.staking_token.lptokens.token0.symbol}-${item.staking_token.lptokens.token1.symbol}`}
                      </Typography>
                    </div>
                  </div>
                </td>
                <td className="duration">
                  <Typography variant="caption" className="lh-1">
                    Duration (seconds)
                  </Typography>

                  <Typography variant="body1" className="lh-1 fw-bold">
                    {item.rewards_duration}
                  </Typography>
                </td>
                <td className="liquidity">
                  <Typography variant="caption" className="lh-1">
                    Supply
                  </Typography>
                  {!item.staking_token.lptokens ? (
                    <Typography variant="body1" className="lh-1 fw-bold">
                      {supply[0]} {item.staking_token.symbol}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body1" className="lh-1 fw-bold">
                        {Number(supply[1])
                          .toFixed(item.staking_token.lptokens.token0.decimals)
                          .replace(/\.?0+$/, "")}{" "}
                        {item.staking_token.lptokens.token0.symbol}
                      </Typography>
                      <Typography variant="body1" className="lh-1 fw-bold">
                        {Number(supply[2])
                          .toFixed(item.staking_token.lptokens.token1.decimals)
                          .replace(/\.?0+$/, "")}{" "}
                        {item.staking_token.lptokens.token1.symbol}
                      </Typography>
                    </>
                  )}
                </td>
                <td className="last-update">
                  <Typography variant="caption" className="lh-1">
                    Last Update
                  </Typography>
                  <Typography variant="body1" className="lh-1 fw-bold">
                    {expired ? (
                      <>
                        {item.last_update_time == 0 ? (
                          "Not Updated"
                        ) : (
                          <Moment unix fromNow>
                            {item.last_update_time}
                          </Moment>
                        )}
                      </>
                    ) : (
                      <Moment unix format="MMM DD, YYYY">
                        {item.last_update_time}
                      </Moment>
                    )}
                  </Typography>
                </td>
                <td className="period">
                  <Typography variant="caption" className="lh-1">
                    Expire
                  </Typography>
                  <Typography variant="body1" className="lh-1 fw-bold">
                    {expired ? (
                      <>
                        {item.period_finish == 0 ? (
                          "Not Started"
                        ) : (
                          <Moment unix fromNow>
                            {item.period_finish}
                          </Moment>
                        )}
                      </>
                    ) : (
                      <Moment unix format="MMM DD, YYYY">
                        {item.period_finish}
                      </Moment>
                    )}
                  </Typography>
                </td>
                <td className="detail">
                  <KeyboardArrowDownIcon className={classes.primaryText} />
                </td>
              </tr>
            </tbody>
          </table>
        </MuiAccordionSummary>
        <MuiAccordionDetails>
          <Grid container spacing={2}>
            {!!rewardPerSeconds && (
              <Grid item xs={12}>
                <Paper elevation={0} className={classes.rewardInfo}>
                  <Grid container>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        Reward Per Seconds : {rewardPerSeconds}{" "}
                        {item.rewards_token.type === "token"
                          ? item.rewards_token.symbol
                          : `${item.rewards_token.lptokens.token0.symbol}-${item.rewards_token.lptokens.token1.symbol}`}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        Remaining Reward : {remainingReward}{" "}
                        {item.rewards_token.type === "token"
                          ? item.rewards_token.symbol
                          : `${item.rewards_token.lptokens.token0.symbol}-${item.rewards_token.lptokens.token1.symbol}`}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}
            <Grid item xs={12}>
              <Paper elevation={0} className={classes.infoBox}>
                <Typography
                  variant="subtitle2"
                  className={classes.infoBoxTitle}
                >
                  {item.rewards_token.type === "token"
                    ? item.rewards_token.symbol
                    : `(${item.rewards_token.lptokens.token0.symbol}-${item.rewards_token.lptokens.token1.symbol})`}
                  -
                  {item.staking_token.type === "token"
                    ? item.staking_token.symbol
                    : `(${item.staking_token.lptokens.token0.symbol}-${item.staking_token.lptokens.token1.symbol})`}{" "}
                  Set Reward
                </Typography>

                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  className={classes.grid}
                >
                  {/* Text Field */}
                  <InputBase
                    value={rewardAmount}
                    onChange={(e) => setRewardAmount(e.target.value)}
                    placeholder="0.0"
                    classes={{
                      root: classes.input,
                      input: classes.inputBase,
                    }}
                  />

                  {/* Button */}

                  <div className={classes.infoLoadingBTNBox}>
                    <LoadingButton
                      loading={loading}
                      valid={isStackButtonEnabled()}
                      success={false}
                      fail={false}
                      onClick={setReward}
                    >
                      {loading
                        ? "processing"
                        : `set
                      ${
                        item.rewards_token.type === "token"
                          ? item.rewards_token.symbol
                          : `${item.rewards_token.lptokens.token0.symbol}-${item.rewards_token.lptokens.token1.symbol}`
                      }
                      reward`}
                    </LoadingButton>{" "}
                  </div>
                </Grid>
                {!!coin1.balance && (
                  <div className="d-flex align-items-center justify-content-end mt-1">
                    <span>
                      Balance:{" "}
                      {Number(coin1.balance)
                        .toFixed(coin1.decimal)
                        .replace(/\.?0+$/, "")}
                    </span>

                    <Fab
                      variant="extended"
                      color="primary"
                      className={classes.fab_max}
                      onClick={() =>
                        setRewardAmount(
                          Number(coin1.balance)
                            .toFixed(coin1.decimal)
                            .replace(/\.?0+$/, "")
                        )
                      }
                    >
                      Max
                    </Fab>
                  </div>
                )}
              </Paper>
            </Grid>
          </Grid>
        </MuiAccordionDetails>
      </MuiAccordion>
    </>
  );
}

StackingListView.propTypes = {};

export default StackingListView;
